<template lang="pug">
  .configuration
    FiveForFive(
      v-if="showFiveForFive"
      :parent="parent"
      :buyRequest="buyRequest"
      @childrenOverride="childrenOverride = $event"
      :childrenOverride="childrenOverride !== null"
      @setChild="setFromSimple($event)"
    )
    .attribute(v-for="attr in attributes.filter(a => !Object.keys(hiddenAttributes).includes(a.code))" :key="attr.code" :data-attr="attr.code" :class="[attr.classes, rhinoConfig2(attr.code) ? 'contents-display' : '']")
      component.edit-attr(
        :is="mode === 'cart' ? 'RogueSelect' : attr.component"
        v-show="!attr.hide"
        :modelValue="isQtyZeroNonDropdown ? '' : config[attr.code]"
        @update:modelValue="updateOptions(attr.code, $event)"
        :code="attr.code"
        :required="true"
        :options="attr.options"
        :label="attr.title"
        :class="microConfig ? 'micro-swatch' : ''"
      )
</template>
<script lang="ts">
import { defineAsyncComponent, defineComponent, nextTick, type PropType } from 'vue'
import { getItemPrice, type IProductConfigurableAttribute } from '@rogue/universal'
import { computePriceSync } from '../../helpers/price-utils'
import { getAttribute } from '../../helpers/attributes'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useProductStore } from '@/js/stores/productStore'
import { usePromoStore } from '@/js/stores/promoStore'
import stockStatus from '@/js/helpers/stockStatus'
import { createBuyRequest , getBuyRequestMeta } from '@/js/helpers/buy-request'
import { MMS_BASE_UNIT_SKU, MMS_ADDON_SKU } from '@/js/pages/product/rackbuilder/utils/mms-utils/mms-constants'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'
import { MS_BASE_UNIT_SKU, MS_ADDON_SKU, withMsSitePrefix } from '@/js/pages/product/rackbuilder/utils/ms-utils/ms-constants'
import { ADDON_CAVE_BUNDLES } from '@/js/pages/product/rackbuilder/products/cave/utils/caveConstants'

const SIZE_SORT = ['XXS', '2XS', 'XS', 'S', 'S/M', 'M', 'L', 'L/XL', 'XL', 'XXL', '2XL', '3XL', '4XL', '1/4"', '5/16"', '3/8"', '1/2"', '3/4"']
const PHYSICAL_GIFTCARD_SKUS = ['GC1500', 'GC1250', 'GC1100', 'GC1050', 'GC1025', 'GC0500', 'GC0250', 'GC0100', 'GC0050', 'GC0025']

type EventProp = {
  attrs: Pick<IProductConfigurableAttribute, 'title' | 'code'>[],
  children: Product[],
}

type AttributeOption = {
  title: string,
  value: string | number | boolean | string[] | number[] | null,
  status: string,
  image?: string,
  imgAttr?: string,
  label?: string,
  disabled?: boolean,
}

type ComponentData = {
  config: { [attributeCode: string]: boolean | number | string | string[] | number[] | null | undefined },
  attributes : any[],
  statusStrings : Record<string, string>,
  price : number | null,
  childrenOverride : EventProp['children'] | null,
}

export default defineComponent({
  name       : 'Configuration',
  components : { FiveForFive: defineAsyncComponent(() => import('./FiveForFive.vue')) },
  inject     : {
    tallestBaseId: {
      from    : 'tallestBaseId',
      default : undefined,
    },
    handleConfigurationUpdate: {
      from    : 'handleConfigurationUpdate',
      default : () => () => { },
    },
    rackHeight: {
      from    : 'rackHeight',
      default : undefined,
    },
  },
  props: {
    buyRequest: {
      type     : Object as PropType<BuyRequest>,
      required : true,
    },
    bundleBuyRequest: {
      type     : Object as PropType<BuyRequest>,
      required : false,
    },
    ignoreAttributes: {
      type    : Array as PropType<string[]>,
      default : () => ([]),
    },
    disabledValues: {
      type    : Object as PropType<{ [key: string]: string[] }>,
      default : () => ({}),
    },
    // Same shape as disabledValues, but purely visual - values still stay clickable/selectable. Used
    // when an option is atypical rather than truly unavailable (e.g. Rhino/Slinger X rack_depth on a 4 post rack).
    grayedOutValues: {
      type    : Object as PropType<{ [key: string]: string[] }>,
      default : () => ({}),
    },
    event: {
      type    : Object as PropType<EventProp>,
      default : null,
    },
    hiddenAttributes: {
      type    : Object as PropType<{ [key: string]: string }>,
      default : () => ({}),
    },
    microConfig: {
      type    : Boolean,
      default : false,
    },
  },
  emits: ['update'],

  setup() {
    const customerStore = useCustomerStore()
    const productStore = useProductStore()
    const promoStore = usePromoStore()

    return {
      customerStore,
      productStore,
      promoStore,
    }
  },

  data: () => ({
    config           : {},
    attributes       : [],
    statusStrings    : {},
    price            : null,
    childrenOverride : null,
  } as ComponentData),

  computed: {
    products() {
      return this.productStore.products
    },

    massStorageSKUs() {
      return {
        BASE_UNIT_SKU : this.isMonsterMassStorage ? MMS_BASE_UNIT_SKU : withMsSitePrefix(MS_BASE_UNIT_SKU, this.$rogue.site.code),
        ADDON_SKU     : this.isMonsterMassStorage ? MMS_ADDON_SKU : withMsSitePrefix(MS_ADDON_SKU, this.$rogue.site.code),
      }
    },

    massStorageUnits(): BuyRequest[] | undefined {
      return this.bundleBuyRequest?.children?.filter(item => {
        const sku = this.productStore.products[item.productId]?.sku

        return sku === this.massStorageSKUs.BASE_UNIT_SKU || sku === this.massStorageSKUs.ADDON_SKU
      })
    },

    is3DStorageProduct(): boolean{
      return this.isMonsterMassStorage || this.isMassStorage
    },

    errors() {
      return this.productStore.errors
    },

    groupId() {
      return this.customerStore.groupId
    },

    lastUpdate() {
      return this.promoStore.lastUpdate
    },

    parent(): Product {
      return this.products[this.buyRequest.productId]
    },

    // If the buy request doesn't have an ID, we are in RHPA.
    mode(): 'cart' | 'rhpa' {
      return this.buyRequest._id ? 'cart' : 'rhpa'
    },

    children(): Product[] {
      if (this.event) {
        return this.event.children
      }

      return this.childrenOverride || (this.parent?.childrenIds || [])
        .map(childId => this.products[childId])
        .filter(child => child !== undefined)
    },

    attrs(): Pick<IProductConfigurableAttribute, 'title' | 'code'>[] {
      return this.event ? this.event.attrs : this.parent?.configurableAttributes || []
    },

    selectedChild(): Partial<Product> {
      if (this.event) {
        return this.event.children.find(c => c.productId === this.buyRequest.children?.[0].productId) || {}
      }

      if (!this.buyRequest.children?.[0].productId) {
        return {}
      }

      return this.products[this.buyRequest.children[0].productId] || {}
    },
    isFirstOfGroup(): boolean {
      if (!this.buyRequest?.metadata?.groupedProductId) {
        return false
      }
      const groupedProduct = this.products[this.buyRequest?.metadata?.groupedProductId]

      return (groupedProduct && groupedProduct.childrenIds?.[0] === this.buyRequest.productId && !groupedProduct.attributes.rogue_children_qty_zero)
    },

    isMonsterMassStorage(): boolean {
      return this.$rogue.featureFlags.isOn('enable-monster-mass-storage')
      && !!this.bundleBuyRequest?.productId
      && (this.productStore.products[this.bundleBuyRequest?.productId]?.sku === MMS_ADDON_SKU
      || this.productStore.products[this.bundleBuyRequest?.productId]?.sku === MMS_BASE_UNIT_SKU)
    },

    isMassStorage(): boolean {
      return this.$rogue.featureFlags.isOn('enable-mass-storage')
      && !!this.bundleBuyRequest?.productId
      && (this.productStore.products[this.bundleBuyRequest?.productId]?.sku === this.massStorageSKUs.ADDON_SKU
      || this.productStore.products[this.bundleBuyRequest?.productId]?.sku === this.massStorageSKUs.BASE_UNIT_SKU)
    },

    showFiveForFive(): boolean {
      return this.$rogue.featureFlags.isOn('fiveforfive_enabled') && this.$rogue.featureFlags.isOn('display_5for5_configurations')
    },

    isQtyZeroNonDropdown() {
      const isQtyZero = this.buyRequest.qty === 0
      const noInventoryBin = !this.selectedChild?.inventory?.bin

      return isQtyZero && noInventoryBin && !this.isFirstOfGroup
    },
  },

  watch: {
    'buyRequest.productId': {
      handler() {
        this.load()
      },
    },
    'buyRequest.children': {
      handler(newVal) {
        const newValue = newVal?.[0]?.productId
        const firstChildId = this.parent?.childrenIds?.[0]
     
        if (
          (!this.parent?.micropackageIds?.length || !this.products[firstChildId as number]) ||
            newValue === undefined
        ) {          
          return
        }

        const newProduct = this.products[newValue]

        if (newProduct?.attributes) {
          Object.keys(this.config).forEach((key) => {
            const attrValue = newProduct.attributes[key]
            if (attrValue !== undefined) {
              this.updateOptions(key, String(attrValue))
            }
          })
        }
      },
      deep      : true,
      immediate : true,
    },
    tallestBaseId: {
      async handler() {
        await nextTick()

        if (!this.is3DStorageProduct) return

        // Skip configurables without an edition attribute (e.g. the Mass Storage crossmember's hanger pack selector), or this forces a bad child match.
        if (!this.attrs.some(attr => attr.code === 'edition')) return

        if (this.tallestBaseId && this.bundleBuyRequest?.metadata?.id === this.tallestBaseId) {
          this.updateOptions('edition', 'No')
        } else if (this.productStore.products[this.bundleBuyRequest.productId]?.sku === this.massStorageSKUs.BASE_UNIT_SKU && !this.tallestBaseId) {
          this.updateOptions('edition', 'No')
        } else if (this.massStorageUnits) { // makes sure to only apply to units and not accessories
          this.updateOptions('edition', 'Yes')
        }
      },
      immediate : true,
      deep      : true,
    },
    lastUpdate: {
      handler() {
        this.updateOptions()
      },
    },
    disabledValues: {
      handler(newVal: { [key: string]: string[] }, oldVal: { [key: string]: string[] }) {
        if (newVal === oldVal) {
          return
        }
        this.updateOptions()
        // If the currently selected option is now disabled, we need to select a different option
        Object.keys(this.disabledValues || {}).forEach(disabledKey => {
          if (this.disabledValues[disabledKey].includes(this.config[disabledKey] as string)) {
            const newValue = this.attributes.find(attr => attr.code === disabledKey)?.options.find(opt => !opt.disabled)?.value
            if (newValue !== undefined) {
              this.updateOptions(disabledKey, newValue)
            }
          }
        })
      },
    },
    grayedOutValues: {
      handler(newVal: { [key: string]: string[] }, oldVal: { [key: string]: string[] }) {
        if (newVal === oldVal) {
          return
        }
        this.updateOptions()
      },
    },
    hiddenAttributes: {
      handler(newVal, oldVal) {
        Object.keys(newVal).forEach(attr => {
          if (newVal[attr] !== oldVal[attr]) {
            this.updateOptions(attr, newVal[attr])
          }
        })
      },
      deep: true,
    },
    childrenOverride() {
      // Override if the children change...
      this.load()
    },
    rackHeight: {
      handler(newVal) {
        if (newVal !== undefined) {
          this.updateOptions('rack_height', newVal)
        }
      },
    },
  },

  created() {
    this.statusStrings = {
      bin : `- ${this.__('Click for Stock Notification')}`,
      oos : `- ${this.__('Out of Stock')}`,
    }

    return this.load()
  },

  methods: {
    // Cave INDY shares one configurable for both sides; createBuyRequest defaults to the first child (LH), 
    // so we correct it to match the bundle option's side after children are loaded.
    correctCaveSideChild(): void {
      if (!this.bundleBuyRequest || !this.buyRequest.children?.[0]) return
      const bundleProduct = this.products[this.bundleBuyRequest.productId]
      if (!ADDON_CAVE_BUNDLES.includes(bundleProduct?.sku)) return

      // Only applies to configurables that expose a 'side' attribute (e.g. INDY, not Side Panel)
      const configurableAttrs = this.parent?.configurableAttributes
      if (!configurableAttrs?.some(a => a.code === 'side')) return

      const bundleOption = bundleProduct?.bundleOptions?.find(
        o => o.id === this.buyRequest.metadata?.bundleOptionId
      )
      const title = bundleOption?.title?.toLowerCase() || ''
      const caveSide = title.includes('right') ? 'right' : title.includes('left') ? 'left' : null

      if (!caveSide || !this.children.length) return

      const currentChild = this.selectedChild

      // Find a child matching the correct side AND all other current attributes (height, depth, etc.)
      // so we don't inadvertently change height or weight stack when correcting the side.
      const correctChild = this.children.find(child => {
        if (child.attributes?.side?.toString()?.toLowerCase() !== caveSide) return false

        return configurableAttrs.every(attr => {
          if (attr.code === 'side') return true
          const currentVal = currentChild?.attributes?.[attr.code]

          return !currentVal || child.attributes?.[attr.code] === currentVal
        })
      })

      // Only mutate if the child actually needs to change to avoid triggering unnecessary reactivity
      if (correctChild && this.buyRequest.children[0].productId !== correctChild.productId) {
        this.buyRequest.children[0].productId = correctChild.productId
      }
    },

    /**
     * Loads all child products and sets the initial configuration
     */
    async load(recursive = false) {
      this.config = {}

      // For cave cable systems, correct the selected child to match the bundle option's side
      // before reading selectedChild — handles both cached children and the post-load case below
      this.correctCaveSideChild()

      // Set the current child BEFORE loading, so that we can show the current option selected before other children are ready
      // This makes it not flash in the RHPA when clicking "Edit"
      if (this.parent && this.selectedChild && this.selectedChild.productId && this.selectedChild.attributes) {
        this.attrs.forEach(attr => {
          if (this.selectedChild?.attributes?.[attr.code]) {
            this.config[attr.code] = this.selectedChild.attributes[attr.code]
          }
        })
      }

      // A hidden attribute is dictated by the host rather than chosen by the customer, so it overrides whichever child the buy request arrived pointing at - the watcher below only covers later changes.
      Object.entries(this.hiddenAttributes).forEach(([code, value]) => this.config[code] = value)

      await this.updateOptions()

      // Now load all the other children and reload if necessary
      if (!this.productStore.allChildrenLoaded(this.buyRequest.productId)) {
        await this.productStore.loadAllChildren(this.buyRequest.productId, this.$rogue)
        this.correctCaveSideChild()
        if (recursive !== true) {
          this.load(true)
        }
      }
    },

    setFromSimple(id: string | number, code = '') {
      const product = this.products[id]
      if (!product) {
        return
      }
      const options = Object.keys(this.config)

      options.forEach(key => this.config[key] = product.attributes[key])
      if(!code || code !== options[0]) {
        this.updateOptions(options[0], product.attributes[options[0]])
      }
      else if(options[1]){
        this.updateOptions(options[1], product.attributes[options[1]])
      }
    },

    /**
     * Gets the stock status for an attribute
     * @param {String} attributeCode
     * @param {String} value
     * @param {Array<Object>} children
     * @return string
     */
    getStockStatusForAttribute(attributeCode, value, children) {
      const statuses = children.filter(child => child.attributes[attributeCode] === value)
        .map(child => stockStatus.forProduct(child))

      if (statuses.some(status => status === '')) {
        return ''
      }

      if (statuses.some(status => status === 'bin')) {
        return 'bin'
      }

      return 'oos'
    },

    /**
     * Gets all possible child products that match a set of attributes
     * @param {Object} mustMatch - { ATTRIBUTE_CODE: VALUE, ... }
     * @return {Array<Object>}
     */
    getPossibleChildren(mustMatch = {}) {
      return this.children.filter(
        child => Object.entries(mustMatch)
          .every(
            ([code, val]) => child.attributes[code] === val
          )
      )
    },

    /**
     * Gets available attribute values for a given attributeCode - in select option format
     * @param attribute
     * @param mustMatch - { ATTRIBUTE_CODE: VALUE, ... }
     * @param type -dropdown, thumbnail, or swatch
     * @param allowNoSelection - Add "Choose an Option" to the dropdown
     * @return {Object}
     */
    getAttributeOptions(attribute, mustMatch = {}, type = 'dropdown', allowNoSelection = false) {
      const attributeCode = attribute.code

      const relevantChildren = this.getPossibleChildren(mustMatch)
      // only find children that actually have this attribute
        .filter(child => child?.attributes[attributeCode])

      const uniqueValues = relevantChildren
      // map to array of values
        .map(child => child?.attributes[attributeCode])
      // clear duplicates
        .filter((value, index, array) => array.indexOf(value) === index)

      // prices will be an array from attribute name ("Red") to an array of the prices for the products with color: red ([100,150,175])
      const prices = {}
      // priceModifiers will be our final object from attribute name to the value to show on the frontend ({red: 5, green: 0})
      const priceModifiers = {}

      // Calculate the prices for each child associated with each possible value
      uniqueValues.forEach(val => prices[val] = relevantChildren
        .filter(child => child.attributes[attributeCode] === val)
        .map(child => {
          if (this.event) {
            return this.price
          }

          return computePriceSync(createBuyRequest(child, this.$rogue), this.$rogue.pinia)
        })
        .sort())

      // Check if the products are Gift cards, and if so don't show the modifiers
      const isPhysicalGiftcard = relevantChildren.find(child => PHYSICAL_GIFTCARD_SKUS.includes(child.sku))

      /**
       * We only want to show modifiers if we can confirm that every time this option is selected, the price goes up
       * by a certain amount.
       *
       * We also don't want to show price modifiers for Gift Cards
       *
       * If the attribute is "Size": S, M, L, XL, and we confirm that XL is $5 more than the lowest price, we show the modifier.
       *
       * If the attribute is "Plate Type", and the options are "None", "Cast", and "Laser", where None has 0 children, Cast has 2, and Laser has 3,
       * we dont want to show the modifier.
       * @type {boolean}
       */
      const ignoreModifiers = Object.values(prices).some((priceVals, index, array) => {
        // Magento config attribute to SHOW the price difference...
        if (this.parent?.attributes?.show_variation_price_difference?.includes(attribute.code)) {
          return false
        }
        // Magento configuration to disable showing the price difference for configu options
        if (this.parent?.attributes?.hide_variation_price_difference === true) {
          return true
        }
        // If this option has a different number of choices than the others, we dont want to show a price modifier
        if (priceVals.length !== array[0].length) {
          return true
        }
        // Since the values are sorted, and arrays are the same length, we can compare if this attribute always adds the same difference
        const diff = priceVals[0] - array[0][0]

        // If any of the values are different, we wont show modifiers.
        return priceVals.some((val, i) => val - array[0][i] !== diff)
      })

      if (!ignoreModifiers && !isPhysicalGiftcard) {
        // In dropdowns - if we are using Dynamic modifier - we want to compare to the currently selected option
        // Dynamic pricing is disabled now
        const allowDynamicPricing = false
        if (type === 'dropdown' && allowDynamicPricing) {
          const selectedPrice = prices[this.config[attributeCode] as string][0]
          Object.keys(prices).forEach(key => priceModifiers[key] = prices[key][0] - selectedPrice)
        } else {
          // Otherwise, we show a modifier related to the minimum option in this row
          const minPrice = Math.min(...Object.values(prices).map(vals => vals[0]))
          Object.keys(prices).forEach(key => priceModifiers[key] = prices[key][0] - minPrice)
        }
      }

      // Now that we have our price modifiers for the top level attribute code, we can create our options array
      const vals = uniqueValues.map(value => {
        // Find the children for this option
        const children = relevantChildren.filter(c => c.attributes[attributeCode] === value)

        const status = this.getStockStatusForAttribute(attributeCode, value, relevantChildren)

        const option: AttributeOption = { title: String(value), value, status }

        if (type === 'thumbnail') {
          // Thumbnail means use the actual child thumbnail. There might be multiple children that could apply; just use the first
          // Find the first child that has a thumbnail
          const childWithThumb = children.find(c => c.rhpaThumbBundle || c.rhpaThumb || c.thumbnail)
          if (childWithThumb) {
            option.image = childWithThumb.rhpaThumbBundle || childWithThumb.rhpaThumb || childWithThumb.thumbnail
          }
        } else if (type === 'colorSwatch') {
          // Setting imgAttr to the attribute ('color' or 'rig_color') will tell Swatches to show as a color or image
          option.imgAttr = attributeCode
        }


        try {
          // label is what shows up in the RHPA swatch. We want to put it through the getAttribute function because sometimes
          // it isn't a 1:1 with the value in the object (like for shoe_size, it might transform into 9.0M / 7.5W)
          option.label = getAttribute(this.parent, children[0], attribute, this.$rogue).value
        } catch (e) {
          option.label = String(value)
        }

        const price = priceModifiers[value]

        if (price && !this.microConfig) {
          option.label += ` (${this.$rogue.currency(price, { plusSymbol: true, euTaxClass: true })})`
        }

        if (status !== '' && type === 'dropdown') {
          const statusString = (this.mode === 'cart' || status === 'oos') ? 'Out of Stock' : 'Click for Stock Notification'
          option.label += ` - ${statusString}`
          if (this.mode === 'cart') {
            option.disabled = true
          }
        }

        if (this.disabledValues && this.disabledValues[attribute.code] && this.disabledValues[attribute.code].indexOf(option.value) !== -1) {
          option.disabled = true
          option.status = 'oos'
        }

        // Grayed out for visual guidance only - stays selectable, unlike disabledValues above.
        // Deliberately NOT 'other-config' - that status also triggers the show-all-for-attribute
        // "reset to nearest valid child" click behavior further up, which would silently change
        // an unrelated attribute (e.g. rack_size) instead of just selecting this option.
        if (this.grayedOutValues && this.grayedOutValues[attribute.code] && this.grayedOutValues[attribute.code].indexOf(option.value) !== -1) {
          option.status = `${option.status ? `${option.status} ` : ''}grayed-hint`
        }

        return option
      })

      // If every selection is a size, sort by that
      if (vals.every(val => SIZE_SORT.indexOf(val.title) !== -1)) {
        vals.sort((a, b) => SIZE_SORT.indexOf(a.title) - SIZE_SORT.indexOf(b.title))
      } else if (vals.every(val => !Number.isNaN(Number.parseInt(val.title, 10)))) {
        vals.sort((a, b) => Number.parseInt(a.title, 10) - Number.parseInt(b.title, 10))
      }

      // Add 'Choose an Option' if this is an accessory product for a grouped page
      if (allowNoSelection && type === 'dropdown') {
        vals.unshift({ title: '', label: this.__('Choose an Option'), value: '', status: 'disabled', disabled: true })
      }

      return vals
    },

    /**
     * Compute the new options, given the current product and configuration
     */
    updateOptions(code?: string, value?: string): void {
      /**
       * First validate that the configuration is still valid - we might have to change an option if its no longer available.
       *
       * For example, if they select Laser Cut Plate on Weight Vest, and previously had a Cast plate selected, we need to unselect it.
       */

      // qty 0 on a grouped child also encodes "out of stock" (buy-request.ts), not just "nothing picked yet".
      if (value && this.buyRequest.qty === 0 && stockStatus.forBuyRequest(this.buyRequest, this.products) === '') {
        this.buyRequest.qty = 1
      }

      let limitOptionsWithChoices = {}

      // Pull off some product attributes from the configurable that modify the display in RHPA
       
      const { use_thumbnail_for_attribute = '', image_swatch_attributes_list = '', show_all_for_attribute = '', hide_config_options = '', use_text_for_attribute = '' } = this.parent?.attributes || {}

      // Whether to show all options, regardless of the previous choices. These will show as grayed out squares, and selecting them will
      // reselect previous options to find one that would work.
      const showAll = (show_all_for_attribute as string).split(',')

      if (code) {
        this.config[code] = value
      }

      /**
       * Normally we select options from the top down: size then color. So if one color is only available in one size, it will hide
       * the colors that do not work.
       *
       * If this is a "ShowAll" attribute, there may not be any valid simples with previous configuration settings.
       * If that happens, we need to wipe out the previous settings and find a new simple
       */
      if (code && showAll.includes(code)) {
        // Find out whether the swatch they clicked is "other-config" status, which means its showing up transparently
        const attributeObj = this.attributes?.find(attr => attr.code === code)
        const swatch = attributeObj?.options?.find(s => s.value === value)

        if (swatch?.status?.includes('other-config')) {
          // Get the default product, and if available, use it to help prioritize which possible child to show
          const defaultProductId = this.parent?.attributes?.rogue_configurable_default
          const defaultProduct = this.products[defaultProductId]

          // Start over from a new simple, instead of doing our normal workflow here.
          const possibleChildren =  this.getPossibleChildren({ [code]: value })
            .map(child => ({
              child,
              // Score each possible child so we can find the one MOST SIMILAR to your current selection
              similarity        : this.attributes?.filter(attr => this.config[attr.code] === child.attributes?.[attr.code])?.length,
              // Score how close you are to the default configuration (tie breaker)
              defaultSimilarity : this.attributes?.filter(attr => defaultProduct?.attributes?.[attr.code] === child.attributes?.[attr.code])?.length,
            }))
            // The defaultSimilarity is just a tie breaker, so make sute the other sort is after this one.
            .sort((a, b) => b.defaultSimilarity - a.defaultSimilarity)
            .sort((a, b) => b.similarity - a.similarity)

          if (possibleChildren.length > 0) {
            this.setFromSimple(possibleChildren[0].child.productId, code)

            // this.setFromSimple will call this function again, so we need to return
            return
          }
          // No valid child exists for this value — fall through to the normal attrs.forEach
          // loop below, which will auto-correct config[code] to a valid option.
        }
      }

      this.attrs.forEach(attribute => {
        const relevantChildren = this.getPossibleChildren(limitOptionsWithChoices)

        // See if the current selected value is valid for the previous options
        if (!relevantChildren.some(child => child.attributes?.[attribute.code] === this.config[attribute.code])) {
          /**
           * Select an actually valid option
           *
           * For instance, if attribute.code === 'PlateSize', and mustMatch.plateType = 'None', this is no longer a valid
           * selection and we need to select a new value (like `PlateSize = 'Vest Only')
           */
          this.config[attribute.code] = relevantChildren[0]?.attributes?.[attribute.code]
        }

        limitOptionsWithChoices[attribute.code] = this.config[attribute.code]
      })

      // Determine which child to use for pricing
      this.updateBuyRequest()

      /**
       * Since Option 1 may restrict the available options for Option 2, we want to track selected options
       * into a reference object that can be used to determine which choices to show for follow-up options.
       *
       * Example:
       * Plate Type: Cast vs Laser Cut,
       * Plate: Cast 10lb, Cast 15lb, Cast 20lb, Laser 12lb, Laser 15lb
       *
       * Selecting "Plate: Cast" filters the Plate option to only show the three Cast options.
       */
      limitOptionsWithChoices = {}

      // Whether to use the simple product thumbnail for the swatch
      const useThumb = String(use_thumbnail_for_attribute)?.split(',')
      // Whether to use the built-in SISU swatch (color or icon) for the attribute
      const useSwatch = String(image_swatch_attributes_list)?.split(',')
      // Whether to force text swatches
      const useText = String(use_text_for_attribute)?.split(',')

      // Update the UI with new swatches / dropdowns / modifiers etc
      const attrs = this.attrs.map((attribute) => {
        const attrCode = attribute.code

        // If this attribute is marked as "Show All Options", we DONT want to restrict it based on previous selections
        const filter = showAll.includes(attrCode) ? {} : limitOptionsWithChoices

        // For accessories to grouped products, we want to allow them to select nothing, which will set qty to 0
        const allowNoSelection = !this.isFirstOfGroup && this.buyRequest.metadata?.groupedProductId

        // If any of the options for this attribute have a long length, we will put it in a dropdown
        const possibleValues = this.children.map(child => child.attributes[attrCode])

        const swatchEnabled = !!this.products[this.buyRequest.productId]?.attributes?.rogue_customcolors_enable
        const fiveCharMax = possibleValues.every(val => (val || '').length <= 5)
        const twentyCharMax = possibleValues.every(val => (val || '').length <= 20)

        // Use swatches if the product is not an optional accessory and either the length is under 5 chars
        // or the config option to enable swatches was selected and the length is under 20. Otherwise use a dropdown.
        let uiType = !allowNoSelection && (fiveCharMax || (swatchEnabled && twentyCharMax)) ? 'swatch' : 'dropdown'
        // Allow magento values to override
        if (useText.includes(attrCode)) {
          uiType = 'swatch'
        } else if (useThumb.includes(attrCode)) {
          uiType = 'thumbnail'
        } else if (useSwatch.includes(attrCode)) {
          uiType = 'colorSwatch'
        }

        if (this.mode === 'cart') {
          uiType = 'dropdown'
        }

        // Get all the options that should be shown for this attribute
        const options = this.getAttributeOptions(attribute, filter, uiType, allowNoSelection)
        // If we are showing all, we need to see what is actually valid with previous selections so we can gray them out
        if (showAll.includes(attrCode)) {
          // If the customer has selected a value for this option, we set it in the limitOptionsWithChoices so that *future*
          // attributes are limited with those values
          
          const showAllLimitOptions = Object.fromEntries(
            Object.entries(this.config).filter(([key]) => key !== attrCode)
          )

          const buyRequest = this.bundleBuyRequest ?? this.buyRequest
          const isIndy = getBuyRequestMeta(buyRequest, this.$rogue)?.groupSku?.includes("INDY")
          const attributeOptions = isIndy ? showAllLimitOptions : limitOptionsWithChoices

          const selectableWithPrevOptions = this.getAttributeOptions(attribute, attributeOptions, uiType, allowNoSelection)
            .map(opt => opt.value)

          options
            .filter(opt => selectableWithPrevOptions.indexOf(opt.value) === -1)
            .forEach(opt => opt.status = `${opt.status ? `${opt.status} ` : ''}other-config`)
        }

        limitOptionsWithChoices[attribute.code] = this.config[attribute.code]

        const firstPossibleChild = this.getPossibleChildren()[0]
        
        const { title, classes } = firstPossibleChild
          ? getAttribute(this.parent, firstPossibleChild, attribute, this.$rogue)
          : { title: attribute.title, classes: undefined }

        return {
          ...attribute,
          options,
          title,
          classes,
          component : uiType === 'dropdown' ? 'RogueSelect' : 'Swatches',
          hide      : false,
        }
      })

      // Hide any matching attributes that have only one available option
      const hideSingleOptions = String(hide_config_options)?.split(',')

      // Remove the corner option from the first bundle of MMS
      if (this.productStore.products[this.bundleBuyRequest?.productId]?.sku === this.massStorageSKUs.BASE_UNIT_SKU && this.$rogue.featureFlags.isOn('enable-monster-mass-storage')) {
        const massWidthAttr = attrs.find(attr => attr.code === 'mass_width')

        if (massWidthAttr) {
          massWidthAttr.options = massWidthAttr.options.filter(option => String(option.title)?.toLowerCase() !== 'corner')
        }
      }

      // Hide edition on Mass Storage units
      if (this.is3DStorageProduct) {
        attrs.forEach(attr => {
          if (this.is3DStorageProduct && ['edition'].includes(attr.code)) {
            attr.hide = true
          }
        })
      }

      const isTheCaveAddon = ADDON_CAVE_BUNDLES.includes(this.$rogue.getProductById(Number(this.bundleBuyRequest?.productId))?.sku)
      const excludeFromCaveCableSystems = ['rack_height', 'side']

      this.attributes = attrs
        .filter(attr => attr.options?.length > 0)
        .filter(attr => this.ignoreAttributes.indexOf(attr.code) === -1)
        // Hardcode just for rhino rack trainer... we need to hide Shrouds if you have Rhino Rack Trainer selected
        // @TODO - remove this code and use hideSingleOptions instead
        .filter(attr => this.config.rig_type !== 'Rhino Rack Trainer' || attr.code !== 'rig_or_wm')
        .filter(attr => !hideSingleOptions.includes(attr.code) || attr.options?.filter(opt => !String(opt.label ?? '').toLowerCase().includes('choose')).length > 1)
        .filter(attr => !(isTheCaveAddon && excludeFromCaveCableSystems.includes(attr.code)))
        
      const noOpts = attrs.filter(attr => !attr.options?.length)

      // add tracking for configuration updates
      this.$rogue.track.track({ type: 'updatedConfiguration', items: [this.buyRequest], selectedSimpleId: this.selectedChild?.productId, selectedSku: this.selectedChild?.sku, activeConfiguration: this.config })

      if (noOpts.length) {
        this.$rogue.log.warn(`Configurable ${this.parent?.productId} - ${this.parent?.name} children ${this.parent?.childrenIds} - had no children with valid attribute for ${noOpts.map(o => o.code)}`)
      }

      if (this.is3DStorageProduct && this.handleConfigurationUpdate && code && value) {
        this.handleConfigurationUpdate()
      }
    },

    /**
     * Read the current configuration, determine the selected child and set its productId
     * Even if its not yet a complete configuration, we want to set the lowest price child id to use for price preview
     */
    updateBuyRequest(): void {
      // Find the children that match every active configuration
      // CYA - there should never be more than 1 child - we force selections in RHPA
      let possibleChildren = this.getPossibleChildren(this.config)
        .sort((childA, childB) => childA.price - childB.price)

      if (possibleChildren.length === 0 && Object.keys(this.config).length > 0) {
        this.$rogue.log.warn(`Unable to find child for configurable ${JSON.stringify(this.config)}`)
        this.config = {}
        possibleChildren = this.children
          .sort((childA, childB) => childA.price - childB.price)
      }

      if (possibleChildren.length === 0) {
        return
      }

      if (!possibleChildren.some(child => child.productId === this.selectedChild.productId)) {
        // Update the v-model of the containing component
        const targetBuyRequest = this.buyRequest.children?.[0]
        if (targetBuyRequest) {
          const assign = createBuyRequest(possibleChildren[0], this.$rogue)
          Object.assign(targetBuyRequest, assign)
          this.$emit('update')
        }
      }

      try {
        this.price = getItemPrice(this.buyRequest, this.groupId, this.$rogue, this.buyRequest)
      } catch (e) {
        this.$rogue.log.warn('Error computing price for configuration.vue', e)
      }
    },
  
    rhinoConfig2(attrCode: string): boolean {
      return this.parent?.sku === 'RM-RHINO-CONFIG-2' && attrCode === 'rig_type'
    },
  },
})
</script>
<style lang="scss">
// Force styles for special attributes

.has-womens-size .swatch-holder {
  width: 33%;

  @include tablet(){
    width: 25%;
  }
}
</style>
<style lang="scss" scoped>

.configuration :deep(.other-config), .configuration :deep(.grayed-hint) {
  opacity: $opacity-disabled;
}

.edit-attr {
  margin-top: $space-xs;
}

.contents-display{
  display: contents;

  .edit-attr {
    grid-row: 1;
  }
}

.micro-configuration:has(.swatches){
  display: grid;
  grid-row: 2 / 4;
  grid-template-rows: subgrid;
  height: 100%;

  @include mobile(){

    .attribute{
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .attribute[data-attr="color"] {
      overflow: hidden;

      :deep(.swatches-fixed .swatch-holder){
        @include mobile(){
          min-width: 33%;
        }

        @media (max-width: 375px) {
          min-width: 46%;
        }
      }
      
      :deep(.swatches){
        overflow-x: auto;
        flex-wrap: nowrap;
      }
    }
  }
}

.micro-configuration.two-items{
  .attribute {
    :deep(.swatches-fixed .swatch-holder){
        @include mobile(){
          min-width: 20%;
        }

        @media (max-width: 530px) {
          min-width: 25%;
        }

         @media (max-width: 420px) {
          min-width: 33%;
          max-width: inherit;
        }
      }
  }
  .attribute[data-attr="color"] {
      :deep(.swatches-fixed .swatch-holder){
        @include mobile(){
          min-width: 20%;
        }

        @media (max-width: 530px) {
          min-width: 25%;
        }

         @media (max-width: 420px) {
          min-width: 33%;
        }
      }
      
    }
}
</style>